/**
 * What the GUEST is told about their order — one vocabulary, one consent rule,
 * for every source (docs/superpowers/specs/2026-09-24-order-source-unification-design.md,
 * items A5 + D2).
 *
 * Before this module there were four order-status → guest-text mappings, and
 * they disagreed:
 *   - the functions order-status notifier said "complete" the moment an order
 *     CLOSED — which a prepaid takeout does at payment, while the food is still
 *     being cooked — and its terminal guard then swallowed the "ready" that
 *     followed, so an in-store prepaid guest was told "complete" and never
 *     "ready";
 *   - the checkout tracker waited for the kitchen (and, for delivery, the
 *     courier) before saying "Completed"/"Delivered";
 *   - the API template picker and the till's staff labels each had their own.
 * And SMS consent was decided two opposite ways: status texts sent unless the
 * guest had refused (`!== false`), while receipts sent only when the guest had
 * opted in (`=== true`), and the till receipt forced `false`.
 *
 * ── The status rule ─────────────────────────────────────────────────────────
 * The guest status is a pure function of the order's FULFILMENT facts —
 * `orderState`, `kitchenState`, `type`, `courierStatus` — and never of where the
 * order came from. `closed` means SETTLED, not FULFILLED: a closed order is
 * "completed" only when the kitchen has nothing left to do (served, or never
 * involved) and, for a delivery, the courier has delivered it or there is no
 * courier leg. `pending` is NOT kitchen-done: a queued ticket is still owed.
 *
 * ── The consent rule ────────────────────────────────────────────────────────
 * A guest may be TEXTED about an order only when `smsConsent === true` — an
 * affirmative opt-in recorded on the order. Absent means "never asked", which
 * is NOT consent: our 10DLC campaign's registered opt-in is the checkout's
 * "Text me order updates" box, so a number that never passed that box is a
 * number outside the campaign, whichever till or webhook it arrived on. Push
 * and email are unaffected — they are not SMS.
 *
 * `apps/functions` cannot import `@order-up/*`; it keeps a mirror
 * (`src/utils/customerOrderMessaging.ts`) whose parity test runs THESE fixture
 * tables, copied verbatim. Change a row here, change it there.
 */

// ─── Vocabulary ─────────────────────────────────────────────────────────────

/**
 * Where the guest's order is, as the guest understands it.
 *
 * `awaitingConfirmation` / `scheduled` / `outForDelivery` are shown (tracker)
 * but never MESSAGED by the status notifier: acceptance is announced as
 * `confirmed`, the scheduled promise was made at checkout, and courier progress
 * is texted by the courier-milestone path, which owns it.
 */
export type CustomerOrderStatus =
  | 'awaitingConfirmation'
  | 'scheduled'
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'outForDelivery'
  | 'completed'
  | 'cancelled';

/** The statuses a guest is MESSAGED about — each one is a registered SMS template. */
export type NotifiableCustomerOrderStatus = 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled';

export const NOTIFIABLE_CUSTOMER_ORDER_STATUSES: readonly NotifiableCustomerOrderStatus[] = [
  'confirmed',
  'preparing',
  'ready',
  'completed',
  'cancelled',
];

export function isNotifiableCustomerOrderStatus(value: unknown): value is NotifiableCustomerOrderStatus {
  return typeof value === 'string' && (NOTIFIABLE_CUSTOMER_ORDER_STATUSES as readonly string[]).includes(value);
}

/** The order facts the guest status is derived from. Source fields are deliberately absent. */
export interface CustomerOrderStatusFacts {
  readonly orderState?: string | null;
  readonly kitchenState?: string | null;
  readonly type?: string | null;
  readonly courierStatus?: string | null;
}

function lower(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase() : '';
}

/** The kitchen has nothing left to do: served it, or was never involved. `pending` is still owed. */
function kitchenDone(kitchen: string): boolean {
  return kitchen === 'served' || kitchen === 'none' || kitchen === '';
}

/**
 * The guest-facing status of an order right now, or null when the order is not
 * a guest-visible order (a till draft not yet committed, a transferred shell).
 */
export function resolveCustomerOrderStatus(order: CustomerOrderStatusFacts): CustomerOrderStatus | null {
  const state = order.orderState ?? '';
  const kitchen = lower(order.kitchenState);
  const courier = lower(order.courierStatus);

  switch (state) {
    case 'cancelled':
    case 'voided':
      return 'cancelled';
    case 'pendingAcceptance':
      return 'awaitingConfirmation';
    case 'scheduled':
      return 'scheduled';
    case 'open':
    case 'held':
    case 'closed':
      break;
    default:
      // draft (not yet placed), transferred (items moved to another order),
      // and anything unknown: nothing to tell the guest.
      return null;
  }

  const closed = state === 'closed';

  if (order.type === 'delivery') {
    if (courier === 'delivered') return 'completed';
    if (courier === 'picked_up' || courier === 'en_route') return 'outForDelivery';
    if (closed && courier === '' && kitchenDone(kitchen)) return 'completed';
    // A delivery the kitchen has finished is still not "ready" to the guest —
    // they cannot collect it. It is being prepared until it leaves.
    if (
      kitchen === 'sent' ||
      kitchen === 'preparing' ||
      kitchen === 'ready' ||
      kitchen === 'served' ||
      courier === 'courier_assigned'
    ) {
      return 'preparing';
    }
    return 'confirmed';
  }

  if (kitchen === 'served' || (closed && kitchenDone(kitchen))) return 'completed';
  if (kitchen === 'ready') return 'ready';
  if (kitchen === 'sent' || kitchen === 'preparing') return 'preparing';
  return 'confirmed';
}

/** Forward order of the progress statuses; a move backwards is never announced. */
const PROGRESS_RANK: Readonly<Record<CustomerOrderStatus, number>> = {
  awaitingConfirmation: 0,
  scheduled: 0,
  confirmed: 1,
  preparing: 2,
  ready: 3,
  outForDelivery: 3,
  completed: 4,
  cancelled: 5,
};

/**
 * Which status (if any) to MESSAGE the guest about for a before → after write.
 *
 *   - Only a CHANGE of guest status is news; the same status twice is silence.
 *   - `cancelled` always goes.
 *   - Leaving `awaitingConfirmation` is the acceptance the guest is waiting
 *     for, announced as `confirmed` even when the same write fired the kitchen.
 *   - Placement itself is not news (a till commit, an order created already
 *     open): from no status, only `ready`/`completed` are announced.
 *   - Firing a scheduled order that the kitchen has not yet received is not
 *     news — it was confirmed at placement, and `preparing` follows the ticket.
 *   - Progress only moves FORWARD; `outForDelivery` is the courier path's to send.
 */
export function resolveCustomerStatusNotification(
  before: CustomerOrderStatusFacts,
  after: CustomerOrderStatusFacts,
): NotifiableCustomerOrderStatus | null {
  const from = resolveCustomerOrderStatus(before);
  const to = resolveCustomerOrderStatus(after);
  if (to === null || to === from) return null;
  if (to === 'cancelled') return 'cancelled';
  if (from === 'cancelled') return null;

  if (from === 'awaitingConfirmation' && (to === 'confirmed' || to === 'preparing')) return 'confirmed';
  if (from === null) return to === 'ready' || to === 'completed' ? to : null;
  if (from === 'scheduled' && to === 'confirmed') return null;
  if (PROGRESS_RANK[to] <= PROGRESS_RANK[from]) return null;
  return isNotifiableCustomerOrderStatus(to) ? to : null;
}

// ─── Consent ────────────────────────────────────────────────────────────────

/**
 * May this guest be sent an SMS about this order? ONE rule, every source:
 * an affirmative, recorded opt-in (`smsConsent === true`). See the module
 * header for why "never asked" is not consent.
 */
export function mayTextCustomerAboutOrder(order: { readonly smsConsent?: unknown }): boolean {
  return order.smsConsent === true;
}

// ─── Fixtures (the contract, as data) ───────────────────────────────────────

/**
 * Every row is run by the canonical test and by the functions mirror's parity
 * test, whose table-identity layer compares these lines verbatim. One row per
 * line; no apostrophes in `why` (the comparison normalises quote style).
 */
export const CUSTOMER_ORDER_STATUS_FIXTURES: ReadonlyArray<{
  readonly before: CustomerOrderStatusFacts;
  readonly after: CustomerOrderStatusFacts;
  readonly status: CustomerOrderStatus | null;
  readonly notify: NotifiableCustomerOrderStatus | null;
  readonly why: string;
}> = [
  { before: { orderState: 'pendingAcceptance' }, after: { orderState: 'open', kitchenState: 'pending' }, status: 'confirmed', notify: 'confirmed', why: 'acceptance' },
  { before: { orderState: 'pendingAcceptance' }, after: { orderState: 'open', kitchenState: 'sent' }, status: 'preparing', notify: 'confirmed', why: 'acceptance that also fired the kitchen is announced as confirmed' },
  { before: { orderState: 'pendingAcceptance' }, after: { orderState: 'cancelled' }, status: 'cancelled', notify: 'cancelled', why: 'denied' },
  { before: { orderState: 'scheduled' }, after: { orderState: 'open', kitchenState: 'sent' }, status: 'preparing', notify: 'preparing', why: 'scheduled order fired to the kitchen' },
  { before: { orderState: 'scheduled' }, after: { orderState: 'open', kitchenState: 'pending' }, status: 'confirmed', notify: null, why: 'fired but the kitchen has not received it yet' },
  { before: { orderState: 'draft' }, after: { orderState: 'open', kitchenState: 'sent' }, status: 'preparing', notify: null, why: 'till commit is placement, not news' },
  { before: { orderState: 'draft' }, after: { orderState: 'closed', kitchenState: 'none' }, status: 'completed', notify: 'completed', why: 'no-kitchen order paid and closed in one write' },
  { before: { orderState: 'open', kitchenState: 'pending' }, after: { orderState: 'open', kitchenState: 'sent' }, status: 'preparing', notify: 'preparing', why: 'ticket reached the kitchen' },
  { before: { orderState: 'open', kitchenState: 'sent' }, after: { orderState: 'open', kitchenState: 'preparing' }, status: 'preparing', notify: null, why: 'sent and preparing are one guest status' },
  { before: { orderState: 'open', kitchenState: 'preparing' }, after: { orderState: 'open', kitchenState: 'ready' }, status: 'ready', notify: 'ready', why: 'kitchen ready' },
  { before: { orderState: 'open', kitchenState: 'ready' }, after: { orderState: 'open', kitchenState: 'served' }, status: 'completed', notify: 'completed', why: 'handed over' },
  { before: { orderState: 'open', kitchenState: 'sent', type: 'takeOut' }, after: { orderState: 'closed', kitchenState: 'sent', type: 'takeOut' }, status: 'preparing', notify: null, why: 'prepaid takeout closed on payment while cooking is NOT complete' },
  { before: { orderState: 'closed', kitchenState: 'sent', type: 'takeOut' }, after: { orderState: 'closed', kitchenState: 'ready', type: 'takeOut' }, status: 'ready', notify: 'ready', why: 'prepaid takeout still gets ready after closing on payment' },
  { before: { orderState: 'closed', kitchenState: 'ready', type: 'takeOut' }, after: { orderState: 'closed', kitchenState: 'served', type: 'takeOut' }, status: 'completed', notify: 'completed', why: 'prepaid takeout completes when handed over' },
  { before: { orderState: 'open', kitchenState: 'pending', type: 'takeOut' }, after: { orderState: 'closed', kitchenState: 'pending', type: 'takeOut' }, status: 'confirmed', notify: null, why: 'a queued ticket is still owed' },
  { before: { orderState: 'open', kitchenState: 'ready', type: 'dineIn' }, after: { orderState: 'closed', kitchenState: 'ready', type: 'dineIn' }, status: 'ready', notify: null, why: 'closing does not outrank the kitchen' },
  { before: { orderState: 'open', kitchenState: 'none', type: 'quickService' }, after: { orderState: 'closed', kitchenState: 'none', type: 'quickService' }, status: 'completed', notify: 'completed', why: 'no kitchen involved: settled is fulfilled' },
  { before: { orderState: 'open', kitchenState: 'preparing', type: 'delivery' }, after: { orderState: 'open', kitchenState: 'ready', type: 'delivery' }, status: 'preparing', notify: null, why: 'a delivery is never ready for pickup' },
  { before: { orderState: 'open', kitchenState: 'ready', type: 'delivery', courierStatus: 'courier_assigned' }, after: { orderState: 'closed', kitchenState: 'ready', type: 'delivery', courierStatus: 'courier_assigned' }, status: 'preparing', notify: null, why: 'closed delivery with a courier leg is not delivered' },
  { before: { orderState: 'closed', kitchenState: 'served', type: 'delivery', courierStatus: 'picked_up' }, after: { orderState: 'closed', kitchenState: 'served', type: 'delivery', courierStatus: 'en_route' }, status: 'outForDelivery', notify: null, why: 'courier progress is the courier path to text' },
  { before: { orderState: 'closed', kitchenState: 'served', type: 'delivery', courierStatus: 'en_route' }, after: { orderState: 'closed', kitchenState: 'served', type: 'delivery', courierStatus: 'delivered' }, status: 'completed', notify: 'completed', why: 'delivered' },
  { before: { orderState: 'open', kitchenState: 'served', type: 'delivery' }, after: { orderState: 'closed', kitchenState: 'served', type: 'delivery' }, status: 'completed', notify: 'completed', why: 'own-driver delivery closed after the kitchen finished' },
  { before: { orderState: 'open', kitchenState: 'sent' }, after: { orderState: 'held', kitchenState: 'sent' }, status: 'preparing', notify: null, why: 'held is staff-internal' },
  { before: { orderState: 'open', kitchenState: 'sent' }, after: { orderState: 'voided', kitchenState: 'sent' }, status: 'cancelled', notify: 'cancelled', why: 'voided reads as cancelled to the guest' },
  { before: { orderState: 'open', kitchenState: 'sent' }, after: { orderState: 'transferred', kitchenState: 'sent' }, status: null, notify: null, why: 'transferred shell' },
  { before: { orderState: 'open', kitchenState: 'ready' }, after: { orderState: 'open', kitchenState: 'sent' }, status: 'preparing', notify: null, why: 'a move backwards is never announced' },
  { before: { orderState: 'cancelled' }, after: { orderState: 'closed', kitchenState: 'none' }, status: 'completed', notify: null, why: 'nothing progresses after cancelled' },
  { before: {}, after: {}, status: null, notify: null, why: 'no facts' },
];

export const SMS_CONSENT_FIXTURES: ReadonlyArray<{
  readonly order: { readonly orderSource?: string; readonly smsConsent?: unknown };
  readonly expected: boolean;
  readonly why: string;
}> = [
  { order: { orderSource: 'online', smsConsent: true }, expected: true, why: 'ticked the checkout opt-in' },
  { order: { orderSource: 'online', smsConsent: false }, expected: false, why: 'declined the checkout opt-in' },
  { order: { orderSource: 'online' }, expected: false, why: 'never asked is not consent' },
  { order: { orderSource: 'pos', smsConsent: true }, expected: true, why: 'till-captured opt-in counts the same' },
  { order: { orderSource: 'pos' }, expected: false, why: 'a keyed phone number is not an opt-in' },
  { order: { orderSource: 'pos', smsConsent: false }, expected: false, why: 'till refusal' },
  { order: { orderSource: 'thirdParty' }, expected: false, why: 'marketplace relay line never saw our opt-in' },
  { order: { orderSource: 'online', smsConsent: 'true' }, expected: false, why: 'only the boolean true is an opt-in' },
  { order: { orderSource: 'online', smsConsent: 1 }, expected: false, why: 'truthy is not true' },
];
